import {
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
} from 'antd';
import {
  ArrowLeftOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  InboxOutlined,
  PlusOutlined,
  ScanOutlined,
  DashboardOutlined,
  UserOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import BreadCrumb from '../modules/breadcrumbs';
import * as partnerApi from '../modules/partners/api';
import PartnerItemsModal from '../components/Modals/PartnerItemsModal';
import './PartnerDetail.scss';

const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  { label: 'Product', value: 'PRODUCT' },
  { label: 'Empty', value: 'EMPTY' },
];

const CATEGORY_COLORS = {
  PRODUCT: 'blue',
  EMPTY: 'orange',
};

const ACTION_STATUS_COLORS = {
  PENDING: 'default',
  START: 'processing',
  RESUME: 'processing',
  PAUSE: 'warning',
  COMPLETE: 'success',
};

const PartnerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partnerForm] = Form.useForm();
  const [itemTypeForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [partner, setPartner] = useState(null);
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemTypesLoading, setItemTypesLoading] = useState(false);

  // Partner edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Item type modal
  const [itemTypeModalOpen, setItemTypeModalOpen] = useState(false);
  const [editingItemType, setEditingItemType] = useState(null);
  const [itemTypeSaving, setItemTypeSaving] = useState(false);
  const [manageItemsType, setManageItemsType] = useState(null);

  // Route history
  const [routeHistory, setRouteHistory] = useState([]);
  const [routeHistoryPagination, setRouteHistoryPagination] = useState({
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [routeHistoryLoading, setRouteHistoryLoading] = useState(false);
  const [routeHistoryLoaded, setRouteHistoryLoaded] = useState(false);

  // Viewers
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersLoaded, setViewersLoaded] = useState(false);
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [editingViewer, setEditingViewer] = useState(null);
  const [viewerSaving, setViewerSaving] = useState(false);
  const [viewerForm] = Form.useForm();

  const breadCrumbs = [{ text: 'PARTNERS', url: '/admin/partners' }, { text: partner?.name || '...' }];

  const fetchPartner = useCallback(async () => {
    setLoading(true);
    const { response } = await partnerApi.getPartner(id);
    if (response) {
      setPartner(response);
    }
    setLoading(false);
  }, [id]);

  const fetchItemTypes = useCallback(async () => {
    setItemTypesLoading(true);
    const { response } = await partnerApi.getPartnerItemTypes(id);
    if (response) {
      setItemTypes(response);
    }
    setItemTypesLoading(false);
  }, [id]);

  const fetchRouteHistory = useCallback(
    async (page = 1) => {
      setRouteHistoryLoading(true);
      const { response } = await partnerApi.getPartnerRoutes(id, { page, take: 20 });
      if (response) {
        setRouteHistory(response.routes || []);
        setRouteHistoryPagination(response.pagination || { totalItems: 0, totalPages: 1, currentPage: page });
      }
      setRouteHistoryLoading(false);
      setRouteHistoryLoaded(true);
    },
    [id],
  );

  useEffect(() => {
    fetchPartner();
    fetchItemTypes();
  }, [fetchPartner, fetchItemTypes]);

  // --- Partner Edit ---
  const handleOpenEditModal = () => {
    partnerForm.setFieldsValue({
      name: partner?.name,
      contactEmail: partner?.contactEmail,
      contactPhone: partner?.contactPhone,
      address: partner?.address,
      notes: partner?.notes,
    });
    setEditModalOpen(true);
  };

  const handleSavePartner = async (values) => {
    setEditSaving(true);
    const { response } = await partnerApi.updatePartner({ id: Number(id), ...values });
    if (response) {
      setPartner(response);
      messageApi.success('Partner updated');
      setEditModalOpen(false);
    } else {
      messageApi.error('Failed to update partner');
    }
    setEditSaving(false);
  };

  // --- Item Type CRUD ---
  const handleOpenItemTypeModal = (itemType = null) => {
    setEditingItemType(itemType);
    if (itemType) {
      itemTypeForm.setFieldsValue({
        name: itemType.name,
        category: itemType.category || 'PRODUCT',
        codePrefix: itemType.codePrefix,
        unitOfMeasure: itemType.unitOfMeasure,
        requiresScan: itemType.requiresScan ?? false,
        requiresPhoto: itemType.requiresPhoto ?? false,
        requiresWeight: itemType.requiresWeight ?? false,
      });
    } else {
      itemTypeForm.resetFields();
      itemTypeForm.setFieldsValue({
        category: 'PRODUCT',
        requiresScan: false,
        requiresPhoto: false,
        requiresWeight: false,
      });
    }
    setItemTypeModalOpen(true);
  };

  const handleSaveItemType = async (values) => {
    setItemTypeSaving(true);
    if (editingItemType) {
      const { response } = await partnerApi.updatePartnerItemType({ id: editingItemType.id, ...values });
      if (response) {
        setItemTypes((prev) => prev.map((t) => (t.id === editingItemType.id ? response : t)));
        messageApi.success('Item type updated');
        setItemTypeModalOpen(false);
      } else {
        messageApi.error('Failed to update item type');
      }
    } else {
      const { response } = await partnerApi.createPartnerItemType(id, values);
      if (response) {
        setItemTypes((prev) => [...prev, response]);
        messageApi.success('Item type created');
        setItemTypeModalOpen(false);
      } else {
        messageApi.error('Failed to create item type');
      }
    }
    setItemTypeSaving(false);
  };

  const handleDeleteItemType = async (itemTypeId) => {
    const { response } = await partnerApi.removePartnerItemType(itemTypeId);
    if (response) {
      setItemTypes((prev) => prev.filter((t) => t.id !== itemTypeId));
      messageApi.success('Item type deleted');
    } else {
      messageApi.error('Failed to delete item type');
    }
  };

  // --- Viewers ---
  const fetchViewers = useCallback(async () => {
    setViewersLoading(true);
    const { response } = await partnerApi.getPartnerViewers(id, { page: 1, take: 100 });
    if (response?.users) {
      setViewers(response.users);
    }
    setViewersLoading(false);
    setViewersLoaded(true);
  }, [id]);

  const handleOpenViewerModal = (viewer = null) => {
    setEditingViewer(viewer);
    if (viewer) {
      const nameParts = (viewer.name || '').split(' ');
      viewerForm.setFieldsValue({
        giveName: nameParts[0] || '',
        familyName: nameParts.slice(1).join(' ') || '',
        email: viewer.email,
        password: '',
      });
    } else {
      viewerForm.resetFields();
    }
    setViewerModalOpen(true);
  };

  const handleSaveViewer = async (values) => {
    setViewerSaving(true);
    if (editingViewer) {
      const updateData = { ...values };
      if (!updateData.password) delete updateData.password;
      const { response } = await partnerApi.updatePartnerViewer(id, editingViewer.id, updateData);
      if (response) {
        fetchViewers();
        messageApi.success('Viewer updated');
        setViewerModalOpen(false);
      } else {
        messageApi.error('Failed to update viewer');
      }
    } else {
      const { response } = await partnerApi.createPartnerViewer(id, values);
      if (response) {
        fetchViewers();
        messageApi.success('Viewer created');
        setViewerModalOpen(false);
      } else {
        messageApi.error('Failed to create viewer');
      }
    }
    setViewerSaving(false);
  };

  const handleDeleteViewer = async (viewerId) => {
    const { response } = await partnerApi.removePartnerViewer(id, viewerId);
    if (response) {
      setViewers((prev) => prev.filter((v) => v.id !== viewerId));
      messageApi.success('Viewer deleted');
    } else {
      messageApi.error('Failed to delete viewer');
    }
  };

  // --- Tab change handler (lazy load route history / viewers) ---
  const handleTabChange = (key) => {
    if (key === 'route-history' && !routeHistoryLoaded) {
      fetchRouteHistory(1);
    }
    if (key === 'viewers' && !viewersLoaded) {
      fetchViewers();
    }
  };

  // --- Table columns ---
  const itemTypeColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => <Tag color={CATEGORY_COLORS[cat] || 'default'}>{cat || '-'}</Tag>,
    },
    {
      title: 'Code Prefix',
      dataIndex: 'codePrefix',
      key: 'codePrefix',
      render: (val) => val || '-',
    },
    {
      title: 'Unit',
      dataIndex: 'unitOfMeasure',
      key: 'unitOfMeasure',
      render: (val) => val || '-',
    },
    {
      title: 'Flags',
      key: 'flags',
      render: (_, record) => (
        <span className="item-type-flags">
          {record.requiresScan && (
            <Tag icon={<ScanOutlined />} color="processing">
              Scan
            </Tag>
          )}
          {record.requiresPhoto && (
            <Tag icon={<CameraOutlined />} color="processing">
              Photo
            </Tag>
          )}
          {record.requiresWeight && (
            <Tag icon={<DashboardOutlined />} color="processing">
              Weight
            </Tag>
          )}
          {!record.requiresScan && !record.requiresPhoto && !record.requiresWeight && '-'}
        </span>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val) => (
        <Tag color={val !== false ? 'green' : 'default'} icon={val !== false ? <CheckCircleOutlined /> : null}>
          {val !== false ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Items',
      key: 'itemsCount',
      render: (_, record) => (record.category === 'EMPTY' ? record._count?.partnerItems ?? 0 : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span className="item-type-actions">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenItemTypeModal(record)}>
            Edit
          </Button>
          {record.category === 'EMPTY' && (
            <Button type="link" size="small" onClick={() => setManageItemsType(record)}>
              Items
            </Button>
          )}
          <Popconfirm
            title="Delete this item type?"
            onConfirm={() => handleDeleteItemType(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  const routeHistoryColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Driver',
      dataIndex: 'driverName',
      key: 'driverName',
    },
    {
      title: 'Fleet',
      dataIndex: 'fleetAccessCode',
      key: 'fleetAccessCode',
    },
    {
      title: 'Status',
      dataIndex: 'actionStatus',
      key: 'actionStatus',
      render: (val) => <Tag color={ACTION_STATUS_COLORS[val] || 'default'}>{val || '-'}</Tag>,
    },
    {
      title: 'Orders',
      dataIndex: 'partnerOrderCount',
      key: 'partnerOrderCount',
      align: 'center',
    },
    {
      title: 'Collected Items',
      dataIndex: 'partnerCollectedItemsCount',
      key: 'partnerCollectedItemsCount',
      align: 'center',
      render: (val) =>
        val > 0 ? (
          <Tag icon={<InboxOutlined />} color="orange">
            {val}
          </Tag>
        ) : (
          '0'
        ),
    },
    {
      title: 'Shift',
      key: 'shift',
      render: (_, record) => {
        const start = record.actualShiftStart
          ? moment(record.actualShiftStart).format('HH:mm')
          : record.shiftStart || '-';
        const end = record.actualShiftEnd ? moment(record.actualShiftEnd).format('HH:mm') : record.shiftEnd || '-';
        return `${start} - ${end}`;
      },
    },
    {
      title: 'Return Drop-off',
      dataIndex: 'returnDropoffConfirmedAt',
      key: 'returnDropoff',
      align: 'center',
      render: (val) =>
        val ? (
          <Tag icon={<CheckOutlined />} color="success">
            Confirmed
          </Tag>
        ) : (
          <CloseOutlined style={{ color: '#bfbfbf' }} />
        ),
    },
  ];

  const viewerColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (val) => val || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenViewerModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this viewer?"
            onConfirm={() => handleDeleteViewer(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <>
          {/* Partner Info Card */}
          <Card
            title="Partner Information"
            extra={
              <Button type="primary" icon={<EditOutlined />} onClick={handleOpenEditModal}>
                Edit
              </Button>
            }
            className="partner-info-card"
          >
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Name">{partner?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Email">{partner?.contactEmail || '-'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{partner?.contactPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Address">{partner?.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="Notes" span={2}>
                {partner?.notes || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Item Types Section */}
          <Card
            title="Item Types"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenItemTypeModal(null)}>
                Add Item Type
              </Button>
            }
            className="item-types-card"
          >
            <Table
              dataSource={itemTypes}
              columns={itemTypeColumns}
              rowKey="id"
              loading={itemTypesLoading}
              pagination={false}
              locale={{ emptyText: 'No item types defined yet' }}
              size="small"
            />
          </Card>
        </>
      ),
    },
    {
      key: 'route-history',
      label: 'Route History',
      children: (
        <Card className="route-history-card">
          <Table
            dataSource={routeHistory}
            columns={routeHistoryColumns}
            rowKey="id"
            loading={routeHistoryLoading}
            locale={{ emptyText: 'No routes found for this partner' }}
            size="small"
            pagination={{
              current: routeHistoryPagination.currentPage,
              total: routeHistoryPagination.totalItems,
              pageSize: 20,
              showSizeChanger: false,
              showTotal: (total) => `${total} routes`,
              onChange: (page) => fetchRouteHistory(page),
            }}
          />
        </Card>
      ),
    },
    {
      key: 'viewers',
      label: (
        <span>
          <UserOutlined /> Viewers
        </span>
      ),
      children: (
        <Card
          title="Partner Viewers"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenViewerModal(null)}>
              Add Viewer
            </Button>
          }
        >
          <Table
            dataSource={viewers}
            columns={viewerColumns}
            rowKey="id"
            loading={viewersLoading}
            locale={{ emptyText: 'No viewers created yet' }}
            size="small"
            pagination={false}
          />
        </Card>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <div className="PartnerDetailPage Content">
        <div className="ContentHeader">
          <BreadCrumb breadCrumbs={breadCrumbs} />
        </div>

        <div className="partner-detail-content">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/partners')}
            className="back-button"
          >
            Back to Partners
          </Button>

          <Tabs defaultActiveKey="overview" items={tabItems} onChange={handleTabChange} />
        </div>

        {/* Edit Partner Modal */}
        <Modal
          title="Edit Partner"
          open={editModalOpen}
          onCancel={() => setEditModalOpen(false)}
          footer={null}
          destroyOnClose
          width={600}
        >
          <Form form={partnerForm} layout="vertical" onFinish={handleSavePartner} requiredMark={false}>
            <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Partner name is required' }]}>
              <Input placeholder="Partner name" />
            </Form.Item>
            <Form.Item label="Contact Email" name="contactEmail" rules={[{ type: 'email', message: 'Invalid email' }]}>
              <Input placeholder="ops@partner.com" />
            </Form.Item>
            <Form.Item label="Contact Phone" name="contactPhone">
              <Input placeholder="+41..." />
            </Form.Item>
            <Form.Item label="Address" name="address">
              <Input placeholder="Partner address" />
            </Form.Item>
            <Form.Item label="Notes" name="notes">
              <TextArea rows={3} placeholder="Internal notes" />
            </Form.Item>
            <Row justify="end" gutter={8}>
              <Col>
                <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
              </Col>
              <Col>
                <Button type="primary" htmlType="submit" loading={editSaving}>
                  Save
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* Item Type Modal */}
        <Modal
          title={editingItemType ? 'Edit Item Type' : 'Add Item Type'}
          open={itemTypeModalOpen}
          onCancel={() => setItemTypeModalOpen(false)}
          footer={null}
          destroyOnClose
          width={520}
        >
          <Form form={itemTypeForm} layout="vertical" onFinish={handleSaveItemType} requiredMark={false}>
            <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Item type name is required' }]}>
              <Input placeholder="e.g. Blue crate, Bottle" />
            </Form.Item>
            <Form.Item label="Category" name="category">
              <Select options={CATEGORY_OPTIONS} placeholder="Select category" />
            </Form.Item>
            <Form.Item label="Code Prefix" name="codePrefix">
              <Input placeholder="e.g. BC-" />
            </Form.Item>
            <Form.Item label="Unit of Measure" name="unitOfMeasure">
              <Input placeholder="e.g. pieces, kg" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="requiresScan" valuePropName="checked">
                  <Checkbox>Requires Scan</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="requiresPhoto" valuePropName="checked">
                  <Checkbox>Requires Photo</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="requiresWeight" valuePropName="checked">
                  <Checkbox>Requires Weight</Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end" gutter={8}>
              <Col>
                <Button onClick={() => setItemTypeModalOpen(false)}>Cancel</Button>
              </Col>
              <Col>
                <Button type="primary" htmlType="submit" loading={itemTypeSaving}>
                  {editingItemType ? 'Update' : 'Create'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* Partner Items Modal */}
        <PartnerItemsModal
          open={!!manageItemsType}
          onClose={() => {
            setManageItemsType(null);
            fetchItemTypes();
          }}
          itemType={manageItemsType}
        />

        {/* Viewer Modal */}
        <Modal
          title={editingViewer ? 'Edit Viewer' : 'Add Viewer'}
          open={viewerModalOpen}
          onCancel={() => setViewerModalOpen(false)}
          footer={null}
          destroyOnClose
          width={520}
        >
          <Form form={viewerForm} layout="vertical" onFinish={handleSaveViewer} requiredMark={false}>
            <Form.Item
              label="First Name"
              name="giveName"
              rules={[{ required: !editingViewer, message: 'First name is required' }]}
            >
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item label="Last Name" name="familyName">
              <Input placeholder="Last name" />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Invalid email' },
              ]}
            >
              <Input placeholder="viewer@partner.com" />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={
                editingViewer
                  ? []
                  : [
                      { required: true, message: 'Password is required' },
                      { min: 6, message: 'Minimum 6 characters' },
                    ]
              }
            >
              <Input.Password placeholder={editingViewer ? 'Leave empty to keep current' : 'Password'} />
            </Form.Item>
            <Row justify="end" gutter={8}>
              <Col>
                <Button onClick={() => setViewerModalOpen(false)}>Cancel</Button>
              </Col>
              <Col>
                <Button type="primary" htmlType="submit" loading={viewerSaving}>
                  {editingViewer ? 'Update' : 'Create'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
};

export default PartnerDetailPage;
